import { Button, Carousel, Col, Dropdown, Row } from 'react-bootstrap';
import React, { SetStateAction, useEffect, useState } from 'react';
import { FaEllipsisV, FaMoneyBillWave } from 'react-icons/fa';
import { Checkbox } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { IParams } from '../../../../../model/shared/IParams';
import { RatingStar } from '../../../../../shared/components/RatingStar';
import { useSearchQuery } from '../../../../../shared/hooks/useSearchQuery';
import { numberFormatter } from '../../../../../utils/helpers/numberFormater';
import { useTranslation } from 'react-i18next';
import { CustomImageHandler } from '../../../../../shared/components/CustomImageHandler';
import {
    clearHtmlFromString,
    formatDate,
    getNumberPosts,
    getSocialIcons,
    truncateString,
} from '../../../../../shared/functions/Functions';
import { priceSplitter } from '../../../../../utils/helpers/priceSplitter';
import { ErrorToast } from '../../../../../utils/toasters';
import { ContractService } from '../service';
import { BookingAcceptContractModal } from './BookingAcceptContractModal';
import { DeclineOfferModal } from '../../../../influencer/offer/modal/offer-modal/components/DeclineOfferModal';
import { ConfirmSendingOfferModal } from '../../../../influencer/offer/modal/offer-modal/components/ConfirmSendingOfferModal';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { BookingContractModal } from './modal/BookingContractModal';
import { IContract } from '../../../../../model/shared/IContract';
import { IDeclineContract } from './dto/IBookingContracts';
import TooltipOverlay from '../../../../../shared/components/tooltip/Tooltip';
import { ConfirmationModal } from '../../../../../shared/components/modal/ConfirmationModal';
import { CheckModalIcon, CrossModalIcon, DeleteIcon, EditIcon } from '../../../../../shared/Icons';

export interface IBookCardProps {
    contracts: IContract[];
    setContracts: React.Dispatch<React.SetStateAction<IContract[]>>;
    acceptContract: (contractId: number) => void;
    declineContract: (contractId: number) => void;
    approveProposal?: (contractId: number) => void;
    declineProposal?: (contractId: number) => void;
    deleteProposal?: (contractId: number) => void;
    selectContract: (contractId: number, isSelected: boolean) => void;
    setBestPickAction: (contractId: number) => void;
    setUpdateContracts: React.Dispatch<React.SetStateAction<boolean>>;
    setDeclineReason: React.Dispatch<SetStateAction<IDeclineContract>>;
}

export function BookingContractsCardItem({
    contracts,
    acceptContract,
    declineContract,
    approveProposal,
    declineProposal,
    deleteProposal,
    selectContract,
    setUpdateContracts,
    setBestPickAction,
    setDeclineReason,
}: IBookCardProps) {
    const { t } = useTranslation();
    const { id: campaignId } = useParams<keyof IParams>();
    const navigate = useNavigate();
    const whoImpersonate = localStorage.getItem('whoImpersonate');
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [isProposalDecline, setIsProposalDecline] = useState(false);
    const [showDeleteProposalModal, setShowDeleteProposalModal] = useState(false);
    const [offerId, setOfferId] = useState(0);
    const [contractData, setContractData] = useState<IContract>();
    const [approveTarget, setApproveTarget] = useState<IContract>();
    const [userId, setUserId] = useState(0);
    const [contractModalStatus, setContractModalStatus] = useState('');
    const {
        user: { isSupervisor, isClient },
    } = useSelector((state: IAllStates) => state.auth);
    const pendingAndNegotiateStatus = ['pending', 'negotiate'];
    const queryUserId = useSearchQuery('userId');
    const queryOfferId = useSearchQuery('offerId');

    const canSelectBestPick = (offerStatus: string) => {
        if ((whoImpersonate || isSupervisor) && pendingAndNegotiateStatus.includes(offerStatus)) {
            return true;
        }
        return false;
    };
    const openModal = (id: number, user: number, negotiateStatus = false) => {
        setOfferId(id);
        setUserId(user);
        setShowBookingModal(true);
        if (negotiateStatus) {
            setContractModalStatus('negotiate');
        }
    };

    const closeModal = () => {
        setShowBookingModal(false);
        setContractModalStatus('');
    };
    const closeConfirmModal = () => {
        setShowConfirmModal(false);
    };

    const openConfirmModal = (id: number, contract: IContract) => {
        setShowConfirmModal(true);
        setOfferId(id);
        setContractData(contract);
    };
    const opeDeclineModal = (id: number, contract: IContract) => {
        setShowDeclineModal(true);
        setIsProposalDecline(false);
        setOfferId(id);
        setContractData(contract);
    };

    const opeProposalDeclineModal = (id: number, contract: IContract) => {
        setShowDeclineModal(true);
        setIsProposalDecline(true);
        setOfferId(id);
        setContractData(contract);
    };

    const declineContractAction = () => {
        if (isProposalDecline) {
            if (declineProposal) {
                declineProposal(offerId ?? 0);
            }
        } else if (declineContract) {
            declineContract(offerId ?? 0);
        }
        setShowDeclineModal(false);
    };

    const approveProposalAction = (id: number) => {
        if (approveProposal) {
            approveProposal(id);
        }
    };

    const openDeleteProposalModal = (id: number) => {
        setShowDeleteProposalModal(true);
        setOfferId(id);
    };

    const deleteProposalAction = () => {
        if (deleteProposal) {
            deleteProposal(offerId ?? 0);
        }
    };

    const acceptContractConfirmModal = () => {
        acceptContract(offerId);
    };

    const openOfferOnNotificationClick = (notificationOfferId: number, notificationUserId: number) => {
        ContractService.getContract(notificationUserId, Number(campaignId))
            .then(response => {
                const selectedOffer = response?.contracts?.[0];
                if (selectedOffer?.isExpired) {
                    setContractModalStatus('negotiate');
                }
                openModal(notificationOfferId, notificationUserId, false);
            })
            .catch(err => ErrorToast(err));
    };

    useEffect(() => {
        if (queryUserId && queryOfferId && campaignId) {
            openOfferOnNotificationClick(Number(queryOfferId), Number(queryUserId));
            navigate(`/brand/campaign/contracts/${campaignId}`);
        }
    }, [queryUserId, queryOfferId, campaignId]);

    return (
        <>
            <BookingContractModal
                show={showBookingModal}
                closeModal={closeModal}
                contractId={offerId}
                modalStatus={contractModalStatus}
                userId={userId}
                acceptContract={acceptContract}
                declineContract={declineContract}
                approveProposal={approveProposal}
                declineProposal={declineProposal}
                deleteProposal={deleteProposal}
                selectContract={selectContract}
                setUpdateList={setUpdateContracts}
                setDeclineReason={setDeclineReason}
            />
            <BookingAcceptContractModal
                action={acceptContractConfirmModal}
                show={showConfirmModal}
                closeModal={closeConfirmModal}
                user={contractData?.user}
                grandTotal={priceSplitter(contractData?.price)}
                nbPosts={getNumberPosts(contractData?.totalPlatforms)}
            />
            <DeclineOfferModal
                date={formatDate(contractData?.createdAt ?? '')}
                declineOffer={declineContractAction}
                show={showDeclineModal}
                closeModal={() => setShowDeclineModal(false)}
                setDeclineReason={setDeclineReason}
                isContract={true}
            />
            <ConfirmSendingOfferModal
                action={() => approveProposalAction(approveTarget?.id ?? 0)}
                grandTotal={priceSplitter(approveTarget?.price)}
                show={!!approveTarget}
                disableButton={false}
                closeModal={() => setApproveTarget(undefined)}
                nbPosts={getNumberPosts(approveTarget?.totalPlatforms)}
                user={approveTarget?.user}
                isContract={true}
                isProposalApproval={true}
            />
            <ConfirmationModal
                show={showDeleteProposalModal}
                closeModal={() => setShowDeleteProposalModal(false)}
                action={deleteProposalAction}
                title={t('brand.campaign.book.offer.delete_proposal')}
                description={t('brand.campaign.book.offer.delete_proposal_confirm')}
            />

            {contracts?.map(contract => {
                const name =
                    contract?.user?.displayName === null
                        ? contract?.user.firstName + ' ' + contract.user.lastName
                        : contract?.user?.displayName;
                const truncatedName = truncateString(name, 20);
                const isTruncated = name.length > 20;
                const proposalStatusClass = contract?.isApproved
                    ? 'proposal-approved'
                    : contract?.isDeclined
                    ? 'proposal-declined'
                    : 'primary';
                const proposalStatusText = contract?.isApproved
                    ? t('brand.campaign.book.offer.approved')
                    : contract?.isDeclined
                    ? t('brand.campaign.book.offer.declined')
                    : t('general.campaign_status.proposed');
                return (
                    <Col key={`${contract?.id}-card-item`} lg={6} className="mb-4">
                        <Row
                            className={`booking-card border-${contract?.openAnnex ? 'pre_annex' : contract.status} ${
                                contract?.isProposed ? 'border-proposed' : ''
                            } m-0 position-relative`}
                        >
                            {contract?.user?.agencyCompany?.id && (
                                <Col md={5} className="offer-by-agency-wrapper">
                                    <div className="offer-by-agency-badge mb-3 mb-md-0">
                                        <img src="/icons/building-icon.svg" alt="Building icon" />
                                        <span className="pl-1">
                                            {t('general.offerByAgency')}
                                            <br />
                                            <span className="text-primary">
                                                {contract?.user?.agencyCompany?.displayName ?? ''}
                                            </span>
                                        </span>
                                    </div>
                                </Col>
                            )}
                            <Col
                                md={5}
                                onClick={() =>
                                    openModal(contract?.id, contract?.user?.id, contract.buttonType === 'Resume')
                                }
                                className="p-0 cursor-pointer "
                            >
                                <Carousel
                                    controls={false}
                                    indicators={false}
                                    id="booking-carousel"
                                    className="dashboard-slider"
                                >
                                    {contract?.user?.feedItems.length > 0 ? (
                                        contract?.user?.feedItems?.map(
                                            item =>
                                                item.postImages.length > 0 && (
                                                    <Carousel.Item key={item.id}>
                                                        <CustomImageHandler
                                                            altTag={item.postImages[0]?.name ?? ''}
                                                            classes="booking-card-image"
                                                            thumbnailPath={item.postImages[0]?.thumbnail ?? ''}
                                                            photoPath={item.postImages[0]?.path ?? ''}
                                                        />
                                                    </Carousel.Item>
                                                )
                                        )
                                    ) : (
                                        <Carousel.Item>
                                            <CustomImageHandler
                                                altTag={contract?.user?.profilePhoto?.name ?? ''}
                                                classes="booking-card-image"
                                                photoPath={contract?.user?.profilePhoto?.thumbnail ?? ''}
                                                thumbnailPath={contract?.user?.profilePhoto?.thumbnail ?? ''}
                                            />
                                        </Carousel.Item>
                                    )}
                                </Carousel>
                                <div className="booking-card-info ">
                                    <div className="position-relative">
                                        <CustomImageHandler
                                            altTag={contract?.user?.profilePhoto?.name ?? ''}
                                            classes={`user-img-rounded cursor-pointer  ${
                                                contract?.isBestPick && 'best-pick-border'
                                            }`}
                                            thumbnailPath={contract?.user?.profilePhoto?.thumbnail}
                                            photoPath={contract?.user?.profilePhoto?.thumbnail ?? null}
                                            name={name}
                                        />
                                    </div>
                                    <div className="ml-2">
                                        <TooltipOverlay content={isTruncated ? name : ''}>
                                            <p className="text-shadow cursor-pointer pl-0">{truncatedName || '-'}</p>
                                        </TooltipOverlay>
                                        {contract?.user?.totalRating ? (
                                            <RatingStar rating={contract?.user?.totalRating} />
                                        ) : (
                                            ''
                                        )}
                                    </div>
                                </div>
                            </Col>
                            <Col md={7} className="d-flex flex-column justify-content-between py-3">
                                <div className="d-flex flex-column justify-content-between">
                                    <div className="d-flex justify-content-between">
                                        <div className="d-flex flex-wrap">
                                            {contract?.totalPlatforms?.map((platform, i) => (
                                                <React.Fragment key={i}>
                                                    {getSocialIcons({
                                                        iconPath: platform.path,
                                                        dataTop: `x${platform.nbPostsTotal}`,
                                                        dataDown: numberFormatter(platform.followers),
                                                        colorText: 'primary',
                                                        parentClasses: 'mr-2',
                                                    })}
                                                </React.Fragment>
                                            ))}
                                        </div>
                                        {/*<div>*/}
                                        {/*    <img*/}
                                        {/*        onClick={*/}
                                        {/*            canSelectBestPick(contract?.status ?? '')*/}
                                        {/*                ? () => setBestPickAction(contract?.id)*/}
                                        {/*                : undefined*/}
                                        {/*        }*/}
                                        {/*        height="25"*/}
                                        {/*        width="25"*/}
                                        {/*        className={`${*/}
                                        {/*            canSelectBestPick(contract?.status) && 'cursor-pointer'*/}
                                        {/*        } ${*/}
                                        {/*            !canSelectBestPick(contract?.status) &&*/}
                                        {/*            !contract.isBestPick &&*/}
                                        {/*            'd-none'*/}
                                        {/*        } mr-2 img-cover`}*/}
                                        {/*        src={*/}
                                        {/*            contract.isBestPick*/}
                                        {/*                ? '/icons/img_best_pick_diamond.png'*/}
                                        {/*                : '/icons/img_best_pick_diamond_grey.png'*/}
                                        {/*        }*/}
                                        {/*        alt="best-pick"*/}
                                        {/*    />*/}
                                        {/*    <Checkbox*/}
                                        {/*        checked={contract?.isSelected}*/}
                                        {/*        onClick={() => selectContract(contract?.id, !contract.isSelected)}*/}
                                        {/*        className={`${*/}
                                        {/*            (contract?.buttonType === 'Open' ||*/}
                                        {/*                contract.buttonType === 'Resume') &&*/}
                                        {/*            'd-none'*/}
                                        {/*        } p-0`}*/}
                                        {/*    />*/}
                                        {/*</div>*/}
                                        <div>
                                            <TooltipOverlay content={t('brand.campaign.book.offer.offer_sent_tooltip')}>
                                                <img
                                                    height="40"
                                                    width="40"
                                                    className={`${
                                                        !contract?.isConvertedToOffer && 'd-none'
                                                    } cursor-pointer`}
                                                    src="/icons/approved-icon.svg"
                                                    alt="Offer sent"
                                                />
                                            </TooltipOverlay>
                                        </div>
                                    </div>
                                    <p
                                        onClick={() =>
                                            openModal(
                                                contract?.id,
                                                contract?.user?.id,
                                                contract.buttonType === 'Resume'
                                            )
                                        }
                                        className="intro  cursor-pointer"
                                    >
                                        {clearHtmlFromString(
                                            !!contract?.description
                                                ? contract?.description
                                                : contract?.user?.about ?? ''
                                        )}
                                    </p>
                                </div>

                                <div>
                                    {!contract?.description && !contract?.user?.about && (
                                        <div className="d-flex mb-2 align-items-center">
                                            <img src="/icons/poll-h.svg" alt="description" />
                                            <div className="ml-2 text-muted">{t('general.no_description')}</div>
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <div className="d-flex align-items-center">
                                                {contract?.isLongTerm ? (
                                                    'PART OF LONG-TERM'
                                                ) : (
                                                    <h4 className="mb-0 euro-symbol euro-symbol text-nowrap">
                                                        {priceSplitter(Math.round(contract?.price))}
                                                    </h4>
                                                )}
                                                {(contract?.openAnnex ||
                                                    contract?.isAnnex ||
                                                    contract?.isRequested ||
                                                    contract?.isProposed) && (
                                                    <p
                                                        className={`mx-2 status ${
                                                            contract?.openAnnex
                                                                ? 'pre_annex'
                                                                : contract?.isAnnex
                                                                ? 'completed'
                                                                : contract?.isProposed
                                                                ? proposalStatusClass
                                                                : 'invite'
                                                        } text-dark`}
                                                    >
                                                        {contract?.openAnnex
                                                            ? `${t('general.offerStatus.pre_annex')} #${
                                                                  contract.openAnnex.annexNumber
                                                              }`
                                                            : contract?.isAnnex
                                                            ? `${t(
                                                                  'brand.campaign.book.contract.modal.annex.label'
                                                              )} #${contract.annexNumber}`
                                                            : contract?.isProposed
                                                            ? proposalStatusText
                                                            : t('general.campaign_status.invite')}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="d-flex align-items-start flex-column">
                                                {!!contract?.additionalPrice && (
                                                    <span className="text-muted d-flex align-items-center mt-1">
                                                        <FaMoneyBillWave className="text-primary mr-1 font-16" />
                                                        <p className="font-9 ">
                                                            {t('brand.campaign.book.offer.additional_costs_included')}
                                                        </p>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex flex-wrap justify-content-end">
                                            {!isClient && contract?.canCounter && (
                                                <Dropdown>
                                                    <Dropdown.Toggle
                                                        className="mr-2"
                                                        variant="light"
                                                        id="dropdown-basic"
                                                    >
                                                        <FaEllipsisV className="text-secondary" />
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <Dropdown.Item
                                                            onClick={() =>
                                                                openModal(contract?.id, contract?.user.id, true)
                                                            }
                                                            className="d-flex align-items-center"
                                                        >
                                                            <i className="fi fi-rr-messages-question icon-fixed me-2"></i>
                                                            {t('brand.campaign.book.offer.negotiate')}
                                                        </Dropdown.Item>
                                                        <Dropdown.Item
                                                            onClick={() => opeDeclineModal(contract?.id, contract)}
                                                            className="d-flex align-items-center danger-hover"
                                                        >
                                                            <CrossModalIcon width={16} height={16} className="me-2" />
                                                            {t('brand.campaign.book.contract.deny_contract')}
                                                        </Dropdown.Item>
                                                        {/* {canSelectBestPick(contract?.status ?? '') && (
                                                            <Dropdown.Item
                                                                onClick={() => setBestPickAction(contract?.id)}
                                                            >
                                                                {contract?.isBestPick
                                                                    ? t(
                                                                          'brand.campaign.book.offer.filters.remove_best_pick'
                                                                      )
                                                                    : t(
                                                                          'brand.campaign.book.offer.filters.set_best_pick'
                                                                      )}
                                                            </Dropdown.Item>
                                                        )} */}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            )}
                                            {isClient &&
                                                contract?.isProposed &&
                                                !contract?.isApproved &&
                                                !contract?.isDeclined && (
                                                    <Dropdown>
                                                        <Dropdown.Toggle
                                                            className="mr-2"
                                                            variant="light"
                                                            id="dropdown-basic"
                                                        >
                                                            <FaEllipsisV className="text-secondary" />
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu>
                                                            <Dropdown.Item onClick={() => setApproveTarget(contract)}>
                                                                <div className="d-flex align-items-center">
                                                                    <CheckModalIcon
                                                                        width={14}
                                                                        height={14}
                                                                        className="me-2"
                                                                    />
                                                                    {t('brand.campaign.book.offer.approve_proposal')}
                                                                </div>
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                onClick={() =>
                                                                    opeProposalDeclineModal(contract?.id, contract)
                                                                }
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    <CrossModalIcon
                                                                        width={14}
                                                                        height={14}
                                                                        className="me-2"
                                                                    />
                                                                    {t('brand.campaign.book.offer.decline_proposal')}
                                                                </div>
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                )}
                                            {!isClient && contract?.isProposed && !contract?.isApproved && (
                                                <Dropdown>
                                                    <Dropdown.Toggle
                                                        className="mr-2"
                                                        variant="light"
                                                        id="dropdown-basic"
                                                    >
                                                        <FaEllipsisV className="text-secondary" />
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        <Dropdown.Item
                                                            onClick={() =>
                                                                openModal(contract?.id, contract?.user?.id, true)
                                                            }
                                                        >
                                                            <EditIcon
                                                                width={20}
                                                                height={20}
                                                                className="dropdown-menu-icon"
                                                            />
                                                            {t('brand.campaign.book.offer.edit_proposal')}
                                                        </Dropdown.Item>
                                                        {!contract?.isDeclined && (
                                                            <Dropdown.Item
                                                                className="text-danger"
                                                                onClick={() => openDeleteProposalModal(contract?.id)}
                                                            >
                                                                <DeleteIcon
                                                                    width={20}
                                                                    height={20}
                                                                    className="dropdown-menu-icon"
                                                                />
                                                                {t('brand.campaign.book.offer.delete_proposal')}
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            )}
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={
                                                    isClient ||
                                                    contract?.buttonType === 'Open' ||
                                                    contract.buttonType === 'Resume'
                                                        ? () =>
                                                              openModal(
                                                                  contract?.id,
                                                                  contract?.user?.id,
                                                                  contract.buttonType === 'Resume'
                                                              )
                                                        : () => openConfirmModal(contract?.id, contract)
                                                }
                                            >
                                                {t(`general.button.${isClient ? 'Open' : contract?.buttonType}`)}
                                            </Button>
                                            {contract?.hasUnreadComments && (
                                                <div
                                                    id="bullet"
                                                    className="red-dot position-relative"
                                                    style={{ top: '-4px', right: '10px' }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Col>
                );
            })}
        </>
    );
}
